import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'
import type {
  ControlLockPolicy,
  ControlLockPolicyUpdate,
  SecurityPolicy,
  SecurityPolicyUpdate,
  SessionPolicy,
  SessionPolicyUpdate,
} from '../queries/settingsPolicies.ts'

/**
 * 관리 › 설정의 정책 셋 — 쓰기(`IFC-RO-88` · `91` · `93` · v0.9.4).
 *
 * 🔴 **셋 다 병합이다**(서버 `model_fields_set` · `session_policy.py` `update_session_policy` · `settings_policy.py` `_update`).
 *
 * | 본문의 칸 | 서버가 하는 일 |
 * | --- | --- |
 * | 뺐다 | 그대로 둔다 |
 * | `null` | **지운다** — 세션은 윗단을, 운영 설정은 기본값을 따른다 |
 * | 값 | 쓴다 |
 *
 * ⇒ 본문은 **고친 칸만** 싣는다. 받은 값 전부를 다시 보내지 않는다(같은 값이면 서버도 그대로지만, 모르는 칸을 덮는 습관을 들이지 않는다).
 * - 거절은 쓰기 «전»에 바뀐 뒤의 실효 조합으로 한다 → 422 `REQUEST_VALIDATION_FAIL` · `context.reason` 이 **문자열 하나**(세션은 `context.scope` 도).
 *   ⚠ 스키마 위반(0 이하 · 정수 아님)은 FastAPI 쪽 422 라 `context.reason` 이 없다 — 화면이 1 이상 정수만 보낸다.
 * - 응답: 고친 뒤의 정책 전체(세션은 `SessionPolicy` 스냅샷).
 * - 역할: admin 한정 · 제어락 비대상(`controlToken.ts` 표에 넣지 않는다).
 * - 세션 정책은 **열린 세션에 소급하지 않는다**(새 세션부터) · 제어락 정책은 다음 획득·heartbeat 부터.
 */

/** 정책 한 묶음의 바꿀 칸 — 값 · `null`(지움). 키를 빼면 그대로 */
export type PolicyChange<T> = Partial<Omit<T, 'command_id'>>

export const useUpdateSessionPolicy = createCommand<
  { scope: string; change: PolicyChange<Omit<SessionPolicyUpdate, 'scope'>>; commandId?: string },
  SessionPolicy
>({
  send: ({ scope, change, commandId }) =>
    orchestrator.request<SessionPolicy>({
      method: 'PUT',
      path: '/settings/session-policy',
      body: { command_id: commandId ?? newCommandId(), scope, ...change },
      retryOn401: true,
    }),
  // 🔴 계정 상세의 실효 값(`keys.users.sessionPolicy`)도 같이 — `users` 아래라 `users.all()` 이 덮는다
  invalidates: () => [keys.policies.session(), keys.users.all()],
})

export const useUpdateSecurityPolicy = createCommand<
  { change: PolicyChange<SecurityPolicyUpdate>; commandId?: string },
  SecurityPolicy
>({
  send: ({ change, commandId }) =>
    orchestrator.request<SecurityPolicy>({
      method: 'PUT',
      path: '/settings/security-policy',
      body: { command_id: commandId ?? newCommandId(), ...change },
      retryOn401: true,
    }),
  invalidates: () => [keys.policies.security()],
})

export const useUpdateControlLockPolicy = createCommand<
  { change: PolicyChange<ControlLockPolicyUpdate>; commandId?: string },
  ControlLockPolicy
>({
  send: ({ change, commandId }) =>
    orchestrator.request<ControlLockPolicy>({
      method: 'PUT',
      path: '/settings/control-lock-policy',
      body: { command_id: commandId ?? newCommandId(), ...change },
      retryOn401: true,
    }),
  invalidates: () => [keys.policies.controlLock()],
})
